import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { formatDateTimeSaudi } from '../../utils/saudiTime';

function isAllowedRole(role) {
  const r = String(role || '').toLowerCase();
  return r === 'admin' || r === 'master_admin' || r === 'custom_role_order_receiver';
}

export default function CashHandoverHistory() {
  const { user } = useAuth();
  const role = user?.role;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError('');
      setLoading(true);
      try {
        const res = await apiService.cashHandover.history();
        if (cancelled) return;
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isAllowedRole(role)) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="bg-white rounded-lg shadow-md border-l-4 border-red-500 p-4">
          <h1 className="text-xl font-bold text-gray-700 mb-1">Cash handover history</h1>
          <p className="text-sm text-gray-600">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2">Cash handover history</h1>
        <p className="text-sm text-gray-600 mb-4">Order receiver sees today only. Admin sees all.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
        )}
        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">No handovers found.</div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold">
                <tr>
                  <th className="px-3 py-2">Day</th>
                  <th className="px-3 py-2">Sales user</th>
                  <th className="px-3 py-2">Cash from orders</th>
                  <th className="px-3 py-2">Cash in hand</th>
                  <th className="px-3 py-2">Variance</th>
                  <th className="px-3 py-2">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-gray-800">{r.dayKey || '—'}</td>
                    <td className="px-3 py-2 text-gray-800">{r.salesUserName || '—'}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-800">SAR {Number(r.cashFromOrders || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-800">SAR {Number(r.cashInHand || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-800">SAR {Number(r.variance || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {r.submittedAt ? formatDateTimeSaudi(r.submittedAt) : '—'}
                      {r.submittedByName ? <span className="text-gray-500"> · {r.submittedByName}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

